
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import Icon from '../misc/Icon';
import PopoverMenu from '../misc/PopoverMenu';
import Popconfirm from '../misc/Popconfirm';
import FieldModes from '../../constants/FieldModes';
import FileViews from '../../constants/FileViews';
import ViewSettings from './settings/ViewSettings';

import ThemeIcons from '../../theme/Icons';
import ThemeContext from '../../theme/ThemeContext';

import { isSettingHidden } from '../../lib/settingsHelper';

const StyledWrapper = styled.div`
  flex: 1;
  max-width: 100%;
  display: flex;
  flex-direction: row;
  min-height: 54px;
`;

const StyledPagesWrapper = styled.div`
  flex: 1;
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  padding-left: 2px;
`;

const StyledPageWrapper = styled.div`
  display: inline-block;
`;

const StyledAddButtonWrapper = styled.div`
  display: flex;
  min-width: 40px;
  justify-content: center;
  align-items: center;
  border-right: 1px solid ${(props) => props.theme.borderColor};
`;

const StyledActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-left: 1px solid ${(props) => props.theme.borderColor};
  padding: 0px 8px;
`;

const StyledSettingsAction = styled.button`
  width: 28px;
  height: 28px;
  margin: 0px 4px;
  padding: 0px;
  background-color: ${(props) => props.theme.backgroundColor};
  color: ${(props) => props.theme.fontColorPrimary};
  border: 0px solid transparent;
  &:hover {
    cursor: pointer;
    svg {
      color: ${(props) => props.theme.primaryColor};
    }
  }

`;

const PageList = ({
  mode,
  view,
  config = {},
  activePageId,
  sortedPages,
  onSelect,
  onDelete,
  onDuplicate,
  onCreate,
  onSettingsClick,
  onViewChange,
  onFileSettingsClick,
  onMoveLeft,
  onMoveRight,
}) => {

  const theme = useContext(ThemeContext);

  const isCreateHidden = isSettingHidden(config, 'create');

  const pageNodes = sortedPages.map((page, i) => (
    <StyledPageWrapper key={page._id}>
      <PageListItem
        mode={mode}
        config={config}
        isMobileView={view === FileViews.mobile}
        pages={sortedPages}
        subtitle={`Page ${i + 1}`}
        title={page.name}
        hidden={page.hidden}
        active={page._id === activePageId}
        pageId={page._id}
        onSelect={onSelect}
        onSettingsClick={onSettingsClick}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onMoveLeft={onMoveLeft}
        onMoveRight={onMoveRight}
      />
    </StyledPageWrapper>
  ));

  return (
    <StyledWrapper>
      {isCreateHidden || mode !== FieldModes.edit || !onCreate ? null : (
        <StyledAddButtonWrapper theme={theme}>
          <Icon
            icon={ThemeIcons.misc.add}
            color={theme.fontColorPrimary}
            hoverColor={theme.primaryColor}
            hoverCursor="pointer"
            size="1x"
            onClick={onCreate}
          />
        </StyledAddButtonWrapper>
      )}
      <StyledPagesWrapper theme={theme}>
        {pageNodes}
      </StyledPagesWrapper>
      {mode !== FieldModes.edit ? null : (
        <StyledActionsWrapper theme={theme}>
          <ViewSettings
            view={view}
            onViewChange={onViewChange}
            theme={theme}
          />
          <StyledSettingsAction
            tabIndex={-1}
            theme={theme}
            onClick={onFileSettingsClick}
          >
            <Icon icon={ThemeIcons.settings.settings} size="lg" />
          </StyledSettingsAction>
        </StyledActionsWrapper>
      )}
    </StyledWrapper>
  );

};

PageList.propTypes = {
  sortedPages: PropTypes.array.isRequired,
  activePageId: PropTypes.string.isRequired,
  mode: PropTypes.string,
  onSelect: PropTypes.func,
  onDelete: PropTypes.func,
  onDuplicate: PropTypes.func,
  onCreate: PropTypes.func,
  onViewChange: PropTypes.func,
  onSettingsClick: PropTypes.func,
  onFileSettingsClick: PropTypes.func,
};

const StyledButtonItem = styled.button` 
  display: flex;
  position: relative;
  padding: 12px 12px 11px 17px;
  white-space: nowrap;
  height: 100%;
  align-items: center;
  transition: all 0.15s ease-in-out;

  color: ${(props) => (props.active ? props.theme.fontColorPrimary : props.theme.fontColorSecondary)};
  background-color: ${(props) => props.theme.backgroundColor};

  z-index: ${(props) => (props.active ? '101' : '100')};

  border-top: 0;
  border-bottom: 0;
  border-left: 0;
  border-right: ${(props) => `0.5px solid ${props.theme.borderColor}`};
  font-weight: ${(props) => (props.active ? 600 : 500)};

  svg {
    color: ${(props) => (props.active ? props.theme.fontColorPrimary : props.theme.fontColorSecondary)};
  }

  &:hover {    
    cursor: grab;
    color: ${(props) => props.theme.fontColorPrimary};
    font-weight: 600;
    svg {
      opacity: 1;
      color: ${(props) => props.theme.fontColorPrimary};
    }
  };

`;

const StyledTitleWrapper = styled.div` 
  display: flex;
  flex: 1;
  flex-direction: row;
  text-align: left;
  margin-right: 7px;

`;

const StyledTitle = styled.div` 
  font-size: 12px;
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledSettingsIcon = styled.span`
  padding-top: 0px;
  &:hover {
    cursor: pointer;
  }
`;

const StyledHiddenIcon = styled.span`
  margin-right: 4px;
  svg {
    display: inline-block;
    color: ${(props) => props.theme.warningColor} !important;
  }
`;

const PageListItem = ({
  pageId,
  pages,
  title,
  hidden,
  subtitle,
  active,
  mode,
  config,
  onSelect,
  onDuplicate,
  onDelete,
  onSettingsClick,
  isMobileView,
  onMoveLeft,
  onMoveRight,
}) => {

  const theme = useContext(ThemeContext);

  const isDuplicateHidden = isSettingHidden(config, 'duplicate');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isMoveLeftHidden = isSettingHidden(config, 'moveLeft');
  const isMoveRightHidden = isSettingHidden(config, 'moveRight');

  const [displayPopover, setDisplayPopover] = useState(false);
  const [displayConfirm, setDisplayConfirm] = useState(false);

  const items = [];

  if (!isDuplicateHidden) {
    items.push({
      icon: { icon: ThemeIcons.settings.duplicate, size: 'sm' },
      text: 'Duplicate',
      onClick: () => {
        onDuplicate(pageId);
        setDisplayPopover(false);
      }
    });
  }

  if (!isDeleteHidden && pages.length > 1) {
    items.push({
      icon: { icon: ThemeIcons.settings.delete, size: 'sm' },
      text: 'Delete',
      onClick: () => setDisplayConfirm(true)
    });
  }

  if (!isMoveLeftHidden && pages.length > 1) {
    items.push({
      icon: { icon: ThemeIcons.settings.moveLeft, size: 'sm' },
      text: 'Move Left',
      onClick: () => {
        onMoveLeft(pageId);
        setDisplayPopover(false);
      }
    });
  }

  if (!isMoveRightHidden && pages.length > 1) {
    items.push({
      icon: { icon: ThemeIcons.settings.moveRight, size: 'sm' },
      text: 'Move Right',
      onClick: () => {
        onMoveRight(pageId);
        setDisplayPopover(false);
      }
    });
  }

  if (mode === FieldModes.edit) {
    items.unshift({
      icon: { icon: ThemeIcons.settings.settings, size: 'sm' },
      text: 'Settings',
      onClick: () => {
        onSettingsClick(pageId);
        setDisplayPopover(false);
      }
    });
  }

  const positions = isMobileView ? ['top'] : ['top']; // preferred positions by priority

  return (
    <StyledButtonItem
      theme={theme}
      data-testid={`page-list-item-${pageId}`}
      onClick={() => onSelect(pageId)}
      active={active}
      tabIndex={-1}
      title={title || subtitle}
    >
      <StyledTitleWrapper>
        {!hidden || pages.length < 2 || mode !== FieldModes.edit ? null : (
          <StyledHiddenIcon theme={theme}>
            <Icon
              icon={ThemeIcons.settings.hide}
              color={theme.warningColor}
              size="sm"
            />
          </StyledHiddenIcon>
        )}
        <StyledTitle active={active}>{title || subtitle}</StyledTitle>
      </StyledTitleWrapper>
      <Popconfirm
        title="Confirm page deletion"
        isOpen={displayConfirm}
        reposition={false}
        positions={positions}
        padding={18}
        cancelText="Cancel"
        onCancel={() => setDisplayConfirm(false)}
        confirmText="Confirm"
        onConfirm={(e) => {
          e.stopPropagation();
          setDisplayConfirm(false);
          onDelete(pageId);
        }}
      >
        <div />
      </Popconfirm>
      {items.length < 1 || mode === FieldModes.readonly ? null : (
        <PopoverMenu
          reposition={false}
          isOpen={displayPopover}
          positions={positions}
          padding={18}
          onClickOutside={() => setDisplayPopover(false)}
          items={items}
        >
          <StyledSettingsIcon onClick={() => setDisplayPopover(!displayPopover)}>
            <Icon
              icon={ThemeIcons.misc.chevronDown}
              hoverCursor="pointer"
              size="1x"
            />
          </StyledSettingsIcon>
        </PopoverMenu>
      )}
    </StyledButtonItem>
  );

};

PageListItem.propTypes = {
  pageId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  mode: PropTypes.string,
  active: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
};

export default PageList;
